import { ChevronDown, Palette, LogOut, Menu, User } from "lucide-react";
import { DistrictContext } from "../../utils/DistrictContext";
import { useState, useEffect, useContext, useRef } from "react";
import { remove } from "../../Libraries/Ajax/HttpService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";

export function Header({
  isDragMode,
  onToggleDragMode,
  isSidebarCollapsed,
  onToggleSidebar,
}) {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("blue");
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const { theme, setTheme, colors } = useTheme();

  const [userData, setUserData] = useState({ name: "", districts: [] });
  const { selectedDistrict, setSelectedDistrict } = useContext(DistrictContext);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

  const themes = [
    { name: "Blue", color: "#00b8f1", value: "blue" },
    // { name: "Pastel", color: "#FFD7A8", value: "pastel" },
    { name: "Teal Green", color: "#4a9b8e", value: "pastel" },
    { name: "White", color: "#FFFFFF", value: "white" },
  ];

  useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth <= 1200) {
      onToggleSidebar(true); // collapse
    } else {
      onToggleSidebar(false); // expand
    }
  };

  // Run once on load
  handleResize();

  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("MCData"));
    if (data) {
      setUserData({
        name: data?.name || "User",
        districts: data?.district || [],
      });
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
        setShowThemeDropdown(false);
        setShowDistrictDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDistrictChange = (distName) => {
    const stored = JSON.parse(localStorage.getItem("MCData")) || {};
    stored.selectedDistrict = distName;
    localStorage.setItem("MCData", JSON.stringify(stored));

    setSelectedDistrict(distName);
    setShowDistrictDropdown(false);
  };

  const handleThemeSelect = (themeValue) => {
    setTheme(themeValue);
    setShowThemeDropdown(false);
  };

  const handleLogout = () => {
    setShowProfileDropdown(false);

    remove("apis/user_logout")
      .then(() => {
        localStorage.clear();
        toast.success("Logged out successfully");
        navigate("/login");
      })
      .catch(() => toast.error("Logout failed"));
  };

  const currentDisplayName =
    userData.districts.find((d) => d.district_name === selectedDistrict)
      ?.district_display_name || "Select District";

const handleProfileSettings = () => {
  setShowProfileDropdown(false); 
  navigate("/profile");          
};

  return (
    <div
      className="border-b px-4 md:px-6 py-3 md:py-4 flex items-center justify-between"
      style={{
        backgroundColor: theme === "white" ? "#f8f9fa" : colors.background,
        borderColor: colors.border,
      }}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
           aria-label="Toggle sidebar"
          className="p-2 rounded-lg transition-all duration-300"
          style={{
            border: `1px solid ${colors.border}`,
            backgroundColor:
              theme === "white" ? "#ffffff" : `${colors.primary}08`,
            color: colors.textSecondary,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = `${colors.primary}15`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              theme === "white" ? "#ffffff" : `${colors.primary}08`;
          }}
        >
          <Menu className="w-5 h-5" style={{ color: colors.text }} />
        </button>
      </div>

      <div className="relative flex items-center gap-6" ref={dropdownRef}>
        {/* District */}
        <div className="relative">
          <div
            className="flex items-center gap-2 px-4 py-2 border border-[#1b3a4b] rounded-lg cursor-pointer hover:bg-[#152535]"
            onClick={() => setShowDistrictDropdown(!showDistrictDropdown)}
            style={{
              borderColor: colors.border,
              color: colors.textSecondary,
              backgroundColor: theme === "white" ? "#ffffff" : "transparent",
            }}
          >
            <span className="text-[#BDB9B5] text-sm">{currentDisplayName}</span>
            <ChevronDown className="w-4 h-4 text-[#BDB9B5]" />
          </div>

          {showDistrictDropdown && (
            <div
              className="absolute left-0 top-12 backdrop-blur-sm border rounded-lg shadow-2xl z-99999 min-w-40 py-2"
              style={{
                backgroundColor: `${colors.background}95`, // Dynamic background color with opacity
                borderColor: colors.border, // Dynamic border color
              }}
            >
              {userData.districts.map((dist) => (
                <div
                  key={dist.id}
                  className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-all hover:bg-${colors.cardBg}/90 ${
                    selectedDistrict === dist.district_name
                      ? `bg-${colors.cardBg}`
                      : ""
                  }`}
                  onClick={() => handleDistrictChange(dist.district_name)}
                >
                  <span className="text-sm" style={{ color: colors.text }}>
                    {dist.district_display_name}
                  </span>
                  {selectedDistrict === dist.district_name && (
                    <span className="ml-auto" style={{ color: colors.primary }}>
                      ✓
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Welcome + Avatar */}
        <div className="flex items-center gap-3 relative">
          <span
            className="text-sm font-bold bg-clip-text text-transparent whitespace-nowrap max-[450px]:hidden"
            style={{
              backgroundImage: colors.gradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome {userData.name}
          </span>

          <div
            className="w-10 h-10 p-2 rounded-full border-2 border-[#00b8f1] overflow-hidden cursor-pointer text-white flex items-center justify-center"
            style={{
              borderWidth: "2px",
              borderStyle: "solid",
              borderColor: colors.primary,
              backgroundImage: colors.gradient,
            }}
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
          >
            <LogOut className="w-6 h-6" />
          </div>

          {showProfileDropdown && (
             <div
              className="absolute right-0 top-12 backdrop-blur-sm border rounded-lg shadow-2xl z-99999 min-w-45 py-2"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {/* Profile Settings */}
              <button
                onClick={handleProfileSettings}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:text-[#595a5c] transition-all"
                style={{color: colors.textSecondary}}
              >
                <User className="w-4 h-4" />
                <span className="text-sm">Profile Settings</span>
              </button>

              {/* Divider */}
              <div className="border-t my-1" style={{ borderColor: `${colors.textSecondary}50` }}></div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:text-[#595a5c] transition-all"
              style={{color: colors.textSecondary}}
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>

        {/* Drag */}
        {/* <button
          onClick={onToggleDragMode}
          className={`p-2 rounded-lg border transition-all duration-300 ${
            isDragMode
              ? "bg-linear-to-r from-[#00b8f1] to-[#0060f1] border-[#00b8f1] text-white"
              : "border-[#2a2520] text-[#BDB9B5] hover:bg-[#1a1510]"
          }`}
        >
          <GripVertical className="w-5 h-5" />
        </button> */}

        {/* Theme */}
        <button
          onClick={() => setShowThemeDropdown(!showThemeDropdown)}
          className="p-2 rounded-lg border transition-all duration-300"
          style={{
            background: colors.gradient,
            borderColor: colors.primary,
            color: "#ffffff",
          }}
          title="Change Theme"
        >
          <Palette className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {showThemeDropdown && (
          <div
            className="absolute right-4 top-14 backdrop-blur-sm border rounded-lg shadow-xl py-2 z-50"
            style={{
               backgroundColor: colors.background,
              borderColor: colors.border,
            }}
          >
            {themes.map((t) => (
              <div
                key={t.value}
                className="px-4 py-3 flex items-center gap-3 cursor-pointer transition-all"
                style={{
                  backgroundColor:
                    theme === t.value
                      ? theme === "white"
                        ? "#f0f0f0"
                        : "#152535"
                      : "transparent",
                }}
                onClick={() => handleThemeSelect(t.value)}
              >
                <div
                  className="w-4 h-4 rounded-full"
                  style={{
                    backgroundColor: t.color,
                    border: t.value === "white" ? "1px solid #ccc" : "none",
                  }}
                />
                <span style={{ color: colors.textSecondary }}>{t.name}</span>
                {theme === t.value && (
                  <span style={{ color: colors.primary }}>✓</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
